import {
  envelope,
  parseInbound,
  PROTOCOL_VERSION,
  type InboundEnvelope,
  type OutboundEnvelope,
} from '@chirpback/protocol';

export type HostListener = (message: InboundEnvelope) => void;

export class HostBridge {
  private listeners = new Set<HostListener>();
  private parentOrigin: string | null = null;

  constructor() {
    window.addEventListener('message', this.onMessage);
  }

  onReady(): void {
    const message = envelope('chirp:ready', { sdkVersion: 'shell-0.1.0' });
    window.parent.postMessage(message, '*');
  }

  subscribe(listener: HostListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  send<T extends OutboundEnvelope['type']>(type: T, payload: Extract<OutboundEnvelope, { type: T }>['payload']): void {
    const message = envelope(type, payload);
    const target = this.parentOrigin ?? '*';
    window.parent.postMessage(message, target);
  }

  destroy(): void {
    window.removeEventListener('message', this.onMessage);
    this.listeners.clear();
  }

  private onMessage = (event: MessageEvent): void => {
    if (event.source !== window.parent) return;
    if (!event.data || typeof event.data !== 'object') return;
    const candidate = event.data as { v?: number };
    if (candidate.v !== PROTOCOL_VERSION) return;
    let parsed: InboundEnvelope;
    try {
      parsed = parseInbound(event.data);
    } catch {
      return;
    }
    if (!this.parentOrigin) this.parentOrigin = event.origin;
    if (event.origin !== this.parentOrigin) return;
    for (const listener of Array.from(this.listeners)) listener(parsed);
  };
}
